import { TrackingHandler } from '../../tracking/TrackingHandler';
import { getUrlParameter } from '../../helpers/browser';
import { getRecaptchaToken } from '../../helpers/captchas';
import { buildFreeTrialPayload, FREE_TRIAL_FORM_NAME_REDIRECT, FREE_TRIAL_FORM_NAME_SHOW_NUMBER, LOGIN_CUSTOMER_TYPES, LOGIN_REDIRECT_DELAY_MS, loginFreeTrialCustomer, submitFreeTrialPayload, toTrackingResultData, } from '../core/free-trial-submission';
import { FormSession } from './FormSession';
const FREE_TRIAL_INPUT_FIELDS = ['name'];
const DEFAULT_GREETING = '[COMPANYNAME], guten Tag, was kann ich für Sie tun?';
const DEFAULT_EXCUSE = 'Ich bin im Meeting';
export class FreeTrialSession extends FormSession {
    showNumber;
    endpoint;
    loginDomain;
    autoLogin;
    submittedPayload = null;
    credentials = null;
    constructor(options) {
        if (options?.mode !== 'redirect' && options?.mode !== 'show-number') {
            throw new Error('FormHandler: free trial mode must be "redirect" or "show-number"');
        }
        const showNumber = options.mode === 'show-number';
        super(options.formName || (showNumber ? FREE_TRIAL_FORM_NAME_SHOW_NUMBER : FREE_TRIAL_FORM_NAME_REDIRECT));
        this.showNumber = showNumber;
        this.endpoint = options.endpoint || window.endpoints.freeTrial;
        this.loginDomain = options.loginDomain || null;
        this.autoLogin = !showNumber && options.autoLogin !== false;
    }
    async submit(data) {
        const formData = await this.prepareData({
            ...data,
            greeting_c: data.greeting_c || DEFAULT_GREETING,
            excuse_c: data.excuse_c || DEFAULT_EXCUSE,
        }, FREE_TRIAL_INPUT_FIELDS);
        TrackingHandler.getInstance().formSubmit(this.formName, formData);
        const payload = await buildFreeTrialPayload(this.state, { ...formData }, this.showNumber);
        payload.captcha_solution = await this.captcha.take();
        this.submittedPayload = payload;
        this.state.verificationHash = undefined;
        return this.send(payload, formData);
    }
    async verify(code) {
        if (!this.submittedPayload || !this.state.verificationHash) {
            throw new Error('FormHandler: verify() requires a submit() that returned "verification-required"');
        }
        const payload = {
            ...this.submittedPayload,
            verification_key: code.trim(),
            verification_hash: this.state.verificationHash,
            rct: await getRecaptchaToken(),
        };
        return this.send(payload, payload);
    }
    async login() {
        if (!this.credentials) {
            throw new Error('FormHandler: login() requires a successful free trial');
        }
        await loginFreeTrialCustomer(this.credentials.username, this.credentials.password, this.loginDomain);
    }
    async send(payload, trackedData) {
        const response = await submitFreeTrialPayload(this.endpoint, payload);
        TrackingHandler.getInstance().formSubmitResult(this.formName, trackedData, response.statusCode, toTrackingResultData(response));
        if (response.result === 'success' && response.data?.verification_hash) {
            this.state.verificationHash = response.data.verification_hash;
            return { status: 'verification-required' };
        }
        if (response.result === 'conflict' || response.statusCode === 409) {
            return { status: 'conflict' };
        }
        if (response.result !== 'success' || response.statusCode < 200 || response.statusCode >= 300) {
            this.captcha.start();
            return {
                status: 'error',
                httpStatus: response.statusCode,
                message: response.message,
                errors: response.errors,
            };
        }
        return this.processSuccess(payload, response);
    }
    processSuccess(payload, response) {
        const customerType = response.data?.customerType;
        this.credentials = {
            username: (payload.email1 || payload.first_name),
            password: response.data?.password || payload.password,
        };
        if (this.autoLogin &&
            customerType &&
            LOGIN_CUSTOMER_TYPES.includes(customerType) &&
            !getUrlParameter('disable-login-redirect')) {
            setTimeout(() => this.login(), LOGIN_REDIRECT_DELAY_MS);
        }
        return {
            status: 'success',
            customerType: customerType || '',
            number: response.data?.number,
            displayNumber: response.data?.displayNumber,
        };
    }
}
